// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * The limits copy.
 *
 * It lives in one exported array because it must appear in three places and be identical in all
 * three: permanently on screen, in `regression-report.md`, and in `diff-report.html`. A limits
 * section that travels with the report is the difference between an honest artefact and one that
 * looks more certain the further it gets from the tool that made it.
 *
 * Tags are limited to <strong> and <code> so the same strings can be stripped to plain text for
 * Markdown without losing meaning.
 */
export const LIMITS_COPY: readonly string[] = [
  '<strong>It does not read.</strong> There is no language model here. Backslide checks objective ' +
    'things: does it parse, does it validate, did it error, did an assertion your own harness ' +
    'recorded flip, did a tool step vanish. If the second run is worse <em>prose</em>, Backslide ' +
    'will say nothing changed, and it will be wrong.',
  '<strong>Edit distance and length are marked “changed, not judged” for a reason.</strong> A big ' +
    'diff is not a bad diff. Use them to sort your reading queue; never as a verdict.',
  '<strong>Token counts come from your file, not from us.</strong> Backslide ships no tokenizer. A ' +
    'blank cell means your export did not carry the number.',
  '<strong>Every row is counted once.</strong> An errored row has an empty output, so it would also ' +
    'fail a JSON check — counting both would double the total. The first check that explains a row ' +
    'owns it, and everything below is marked not-counted.',
  '<strong>Errors are separated from regressions.</strong> A run that hit a rate limit did not get ' +
    'worse because of your prompt. The fix is to re-run, so those rows get their own line.',
  '<strong>An extra tool step is not a regression.</strong> A longer path is not a worse path. Only ' +
    'a step that was replaced or vanished counts.',
  '<strong>The join can be wrong, and it tells you when it might be.</strong> Every match states how ' +
    'it was made. If the ids matched but the inputs did not, Backslide throws the ids away and ' +
    'joins on content instead, and says so.',
  '<strong>Rows with identical inputs cannot be told apart by any method.</strong> Those are ' +
    'reported as a group with a net change, and are deliberately excluded from the per-row count — ' +
    'because pairing them at all is a guess, and the count would depend on which guess you made.',
  '<strong>Unmatched is not a regression.</strong> A row in the before run with no partner in the ' +
    'after run is reported as dropped and counted separately.',
  '<strong>Over-normalising hides bugs silently; under-normalising floods you loudly.</strong> That ' +
    'asymmetry is why the argument rules are conservative and why sorting arrays is off. Watch the ' +
    'collision counter beside the alphabet.',
  '<strong>This is a snapshot, not CI.</strong> It does not gate your pull request and it will not ' +
    'run tomorrow. If you need this check every time, put it in your pipeline — ' +
    '<code>promptfoo eval --filter-failing</code> will re-run everything failing in the second run, ' +
    'though not the narrower set Backslide gives you: what passed before and fails now.',
];

export const PRIVACY_PROTECTED: readonly string[] = [
  'Both files are read, joined, scored and exported entirely inside this browser tab. There is no ' +
    'code path in Backslide that puts your file content into a network request.',
  'There is no account, no upload, no queue and no retention, because there is nowhere for any of ' +
    'those to live. The tool is a static page.',
  'Nothing about your files is written to storage. The only things kept between visits are your ' +
    'theme choice and your threshold settings, under the keys <code>backslide.theme</code> and ' +
    '<code>backslide.config</code>.',
];

export const PRIVACY_NOT_PROTECTED: readonly string[] = [
  'The page itself is served by GitHub Pages, which logs the request for the page, as any host ' +
    'would. That is the page, not your data.',
  'Anything you paste into the feedback form is sent when you press Send. Do not paste a real ' +
    'customer prompt into it.',
  'The artefacts you download are yours to handle. <code>regression.csv</code> and the reports ' +
    'quote your model’s output verbatim — that is the point of them, and it means they are as ' +
    'sensitive as the files you started with.',
];

export const PRIVACY_TRUST: readonly string[] = [
  'The static bundle served by GitHub Pages, and the TLS chain to it.',
  'A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, ' +
    'no cross-site tracking; your files and data are never sent to it.',
  'Feedback you choose to send is sent to <code>feedback.benrichardson.dev</code>. Nothing is sent ' +
    'unless you open the feedback form and press Send; your files and data never are.',
  'Those two scripts are the <em>only</em> third-party origins this page may contact, and the ' +
    'Content-Security-Policy in the page header is where that is enforced rather than promised — ' +
    'open the source and read it, it is one line. Backslide does not claim to make no network ' +
    'requests at all; it claims that none of them can carry your files, and the policy is how you ' +
    'check that yourself.',
];
